import React, { useMemo } from 'react';
import WelcomeSheet from '@/modules/topic/welcome/WelcomeSheet';
import ShareIntroBlock from '@/modules/topic/welcome/ShareIntroBlock';
import HomeInstallNudgeBlock from '@/modules/topic/welcome/HomeInstallNudgeBlock';
import HomeInstallDetailBlocks from '@/modules/topic/welcome/HomeInstallDetailBlocks';
import { NEWS_BODY_EXCERPT_CHARS } from '@/modules/config';
import { TopicLogic } from '@/modules/topic/logic';
import ResharePromptBlock from '@/modules/topic/welcome/ResharePromptBlock';
import WelcomeButton from '@/modules/topic/welcome/WelcomeButton';
import { useHomeInstallNudge } from '@/modules/topic/welcome/useHomeInstallNudge';
import type { TopicWelcomeProps } from '@/modules/topic/welcome/types';
import { ReshareStoryLogic } from '@/modules/share/reshare_story_logic';
import { useNewsReshareStory } from './newsReshareStory';
import { HeadlineAndArticleExtractor } from './headlineAndArticleExtractor';
import styles from './WelcomeInterstitial.module.css';

/**
 * News welcome sheet — the divergent one. A news link travels by direct text
 * from person to person, so a recipient arriving on one is met with the story
 * that traveled with it (#447): who relayed it, the chain of connections behind
 * them, the news itself (its headline, then the byline, dateline and article
 * excerpt printed by the topic's own intro form just as the text printed
 * them), how far the run has come against its goal, and the CTA that passes
 * it on.
 *
 * With no leg to tell that story — a news topic opened directly, or a leg whose
 * payload can't be read — the sheet falls back to the standard share intro the
 * other types show.
 */
const WelcomeInterstitial: React.FC<TopicWelcomeProps> = ({
  isOpen,
  topicType,
  senderName,
  onDismiss,
  hideShareIntro = false,
  isUpdate = false,
  topic,
  topicId,
  legId,
  receivedUpdateReshare = null,
}) => {
  const {
    payload,
    settled,
    reshareStory,
    goalSentence,
    goalReached,
    limitReached,
    canKeepRolling,
  } = useNewsReshareStory(legId, topic);
  // The sheet opens once the leg probe settles, so it opens at the height and
  // with the content it keeps, rather than growing into a story mid-animation.
  const open = isOpen && settled;
  const homeInstall = useHomeInstallNudge(open);

  // "_" is the anonymous-sender sentinel the _topicUrl primitive writes; with
  // no name the personal connection is still worth crediting, since this link
  // only ever travels by direct text.
  const relayedBy = senderName && senderName !== '_' ? senderName : undefined;

  const showStory = !hideShareIntro && payload !== null && !payload.gated;
  // The news as the intro text laid it out, in the sharing mode of the leg
  // it arrived on: a leg telling a reshare story is a reshare. The headline
  // shows when the text printed one; a form that never prints the article
  // falls back to a plain excerpt of the body.
  const news = useMemo(() => {
    if (!showStory || !topic) {
      return { headline: null, articleWithBylineDateline: '' };
    }
    const sharingMode = reshareStory ? 'reshare' : 'fresh';
    const { headline, articleWithBylineDateline } =
      HeadlineAndArticleExtractor.extract(topic, sharingMode);
    return {
      headline,
      articleWithBylineDateline:
        articleWithBylineDateline ??
        (topic.body
          ? TopicLogic.bodyExcerpt(topic.body, NEWS_BODY_EXCERPT_CHARS)
          : ''),
    };
  }, [showStory, topic, reshareStory]);
  // The reshare CTA needs a still-open run and the leg to pass on.
  const showKeepRolling =
    showStory && canKeepRolling && !!topic && !!topicId && !!legId;

  return (
    <WelcomeSheet
      isOpen={open}
      onDismiss={onDismiss}
      compact={!homeInstall.show && !showStory}
      tall={showStory}
    >
      {showStory ? (
        <>
          <p className={styles.relayIntro} data-testid="news-welcome-intro">
            {relayedBy ? (
              <>
                Your contact <strong>{relayedBy}</strong>
              </>
            ) : (
              'One of your contacts'
            )}{' '}
            relayed this message through Snowball, a web app that makes it easy
            to spread the word:
          </p>
          {reshareStory && (
            <p
              className={styles.reshareStory}
              data-testid="news-welcome-reshare-story"
            >
              {reshareStory}
            </p>
          )}
          {(news.headline || news.articleWithBylineDateline) && (
            <article className={styles.news}>
              {news.headline && (
                <h1
                  className={styles.headline}
                  data-testid="news-welcome-headline"
                >
                  {news.headline}
                </h1>
              )}
              {news.articleWithBylineDateline && (
                <p className={styles.newsBody} data-testid="news-welcome-body">
                  {news.articleWithBylineDateline}
                </p>
              )}
            </article>
          )}
          {payload.impressionGoal != null && (goalReached || goalSentence) && (
            <p className={styles.progress} data-testid="news-welcome-progress">
              {goalReached
                ? `This run reached its goal of ${ReshareStoryLogic.peoplePhrase(payload.impressionGoal)}! 🎉`
                : goalSentence}
            </p>
          )}
          {limitReached && (
            <p className={styles.progress} data-testid="news-welcome-complete">
              This news has reached its sharing limit, but you can still check
              out what it&apos;s all about.
            </p>
          )}
        </>
      ) : (
        !hideShareIntro && (
          <ShareIntroBlock
            topicType={topicType}
            senderName={senderName}
            isUpdate={isUpdate}
          />
        )
      )}

      {homeInstall.show && (
        <HomeInstallNudgeBlock
          dismissed={homeInstall.dismissed}
          onDismissedChange={homeInstall.onDismissedChange}
        />
      )}

      {showKeepRolling && (
        <ResharePromptBlock
          topic={topic}
          topicId={topicId}
          previousLegId={legId}
          receivedUpdateReshare={receivedUpdateReshare}
          onDismiss={onDismiss}
        />
      )}

      <WelcomeButton
        onClick={onDismiss}
        label={showKeepRolling ? 'Read the full news story' : 'Continue'}
        fill={showKeepRolling ? 'clear' : undefined}
        testId="news-welcome-continue"
      />

      {homeInstall.show && <HomeInstallDetailBlocks />}
    </WelcomeSheet>
  );
};

export default WelcomeInterstitial;
